/**
 * Use Case: Should Filter Out Blocked Or Hidden Media
 *
 * Mirrors the three client-side guards duplicated in every read method of
 * Flutter's content_data_source.dart (see getMediaByPath, getMediaByShareLink,
 * getUserMedias, getLikedContent, getSavedContent, getUnlockedContent,
 * getContentByHashTag).
 *
 * Guards:
 *  1. Drop if `media.blocked` and the viewer is not the owner.
 *     (For paginated flows Flutter applies this server-side via
 *     `where('blocked', isEqualTo: false)`, so the client-side check is a
 *     no-op there — applying it unconditionally is safe and adds defence in
 *     depth.)
 *  2. Drop if the media owner appears in `currentUser.usersBlockedMe`.
 *  3. Drop if `media.id` appears in `currentUser.hiddenContent`. The
 *     cloud-function `hideContent` (`hide_content.ts`) writes the contentId
 *     (i.e. media id) into `hiddenContent`, so the local filter must key off
 *     `media.id`. Flutter's two single-item lookups (getMediaByPath,
 *     getMediaByShareLink) currently check `media.firebaseId` instead — that
 *     is a bug and is being fixed in lockstep here.
 */

import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export class ShouldFilterOutBlockedOrHiddenMediaUseCase {
    execute(
        media: UserMediaEntity,
        currentUser: UserEntity | null,
    ): boolean {
        // Guard (1): blocked content is hidden from non-owners
        if (media.blocked && currentUser?.firebaseId !== media.firebaseId) {
            return true;
        }

        if (!currentUser) {
            return false;
        }

        // Guard (2): drop content from creators who have blocked the current user
        if (
            currentUser.usersBlockedMe
            && media.firebaseId
            && currentUser.usersBlockedMe.includes(media.firebaseId)
        ) {
            return true;
        }

        // Guard (3): drop hidden content (keyed by media id, matching the
        // hideContent cloud function source of truth).
        if (currentUser.hiddenContent && currentUser.hiddenContent.includes(media.id)) {
            return true;
        }

        return false;
    }
}

export const shouldFilterOutBlockedOrHiddenMediaUseCase = new ShouldFilterOutBlockedOrHiddenMediaUseCase();
